import { LitElement, html, type CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import styles from './styles.js';

// ══════════════════════════════════════════════════════════════════════════════
// uwc-toolbar
// ══════════════════════════════════════════════════════════════════════════════

/**
 * A horizontal layout strip that groups related controls into three named
 * regions: **start** (leading), **center**, and **end** (trailing). A thin
 * separator is rendered between regions that have content.
 *
 * @slot start   - Leading actions (left-aligned, grows to fill space).
 * @slot center  - Center content (shrinks to content width).
 * @slot end     - Trailing actions (right-aligned, grows to fill space).
 *
 * @csspart toolbar    - The toolbar `<div>` element.
 * @csspart start      - Start section wrapper.
 * @csspart center     - Center section wrapper.
 * @csspart end        - End section wrapper.
 * @csspart separator  - Vertical separator `<span>` elements.
 *
 * @cssprop --uwc-toolbar-bg               - Background color. Default: surface.
 * @cssprop --uwc-toolbar-border           - Border shorthand.
 * @cssprop --uwc-toolbar-radius           - Border radius.
 * @cssprop --uwc-toolbar-shadow           - Box-shadow.
 * @cssprop --uwc-toolbar-padding          - Outer padding.
 * @cssprop --uwc-toolbar-gap              - Gap between the three sections.
 * @cssprop --uwc-toolbar-section-gap      - Gap between items within a section.
 * @cssprop --uwc-toolbar-min-height       - Minimum toolbar height. Default: 3rem.
 * @cssprop --uwc-toolbar-separator-color  - Separator color.
 * @cssprop --uwc-toolbar-separator-width  - Separator width. Default: 1px.
 * @cssprop --uwc-toolbar-separator-height - Separator height. Default: 1.5rem.
 */
export class UwcToolbar extends LitElement {
  static override styles: CSSResultGroup = styles;

  /**
   * Accessible label for the toolbar region.
   * Rendered as `aria-label` on the root element.
   */
  @property({ attribute: 'aria-label' })
  override ariaLabel = '';

  // Track which slots have content so we can hide separators when empty
  @state() private _hasStart  = false;
  @state() private _hasCenter = false;
  @state() private _hasEnd    = false;

  // ── Slot change handlers ─────────────────────────────────────────────────

  private _onStartSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this._hasStart = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _onCenterSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this._hasCenter = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _onEndSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this._hasEnd = slot.assignedNodes({ flatten: true }).length > 0;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  protected override render() {
    // Show separator between start and center only when both have content
    const showSep1 = this._hasStart && this._hasCenter;
    // Show separator between center and end only when both have content
    const showSep2 = (this._hasStart || this._hasCenter) && this._hasEnd;

    return html`
      <div
        part="toolbar"
        class="uwc-toolbar"
        role="toolbar"
        aria-label=${this.ariaLabel || 'Toolbar'}
      >
        <!-- ── Start ─────────────────────────────────────────────────── -->
        <div part="start" class="uwc-toolbar__start">
          <slot name="start" @slotchange=${this._onStartSlotChange}></slot>
        </div>

        <!-- ── Separator 1 ───────────────────────────────────────────── -->
        ${showSep1
          ? html`<span part="separator" class="uwc-toolbar__separator" role="separator" aria-orientation="vertical"></span>`
          : ''}

        <!-- ── Center ────────────────────────────────────────────────── -->
        <div part="center" class="uwc-toolbar__center">
          <slot name="center" @slotchange=${this._onCenterSlotChange}></slot>
        </div>

        <!-- ── Separator 2 ───────────────────────────────────────────── -->
        ${showSep2
          ? html`<span part="separator" class="uwc-toolbar__separator" role="separator" aria-orientation="vertical"></span>`
          : ''}

        <!-- ── End ───────────────────────────────────────────────────── -->
        <div part="end" class="uwc-toolbar__end">
          <slot name="end" @slotchange=${this._onEndSlotChange}></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-toolbar': UwcToolbar; }
}
